import React, {useContext, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import Modal from "../../components/Modal.jsx";
import useDownloadState from "../../api/utilities/useDownloadState.js";
import FormError from "../../components/form/FormError.jsx";
import {ProjectApi} from "../../api/authority/generated/index";
import {GlobalContext} from "../../utils/context/GlobalContextProvider.jsx";
import toast from "react-hot-toast";
import Button from "../../components/Button.jsx";
import ProjectRoleSelect from "./ProjectRoleSelect.jsx";
import {buildProjectRoleValues, OWNER_ROLE_VALUE} from "./projectRoleOptions.js";
import {useIsAdministrator} from "../../components/auth/rolesAndPermissions.js";

export default function ProjectInvitationButton({project, projectRoles = [], onFinally}) {

    let {getAuthorityConfiguration, resetCurrentUserProjects} = useContext(GlobalContext);
    let {t} = useTranslation();

    let [modalOpen, setModalOpen] = useState();

    let [invitePressed, setInvitePressed] = useState(false);
    let [email, setEmail] = useState('');
    const roleOptions = buildProjectRoleValues(projectRoles, t);
    const noRoleOption = roleOptions.find(({value}) => value === null) ?? null;
    const [selectedRole, setSelectedRole] = useState(noRoleOption);
    const isAdministrator = useIsAdministrator();
    const canAssignAccess = project.isOwner || isAdministrator;

    let [state, setDownloading, setResult, setError] = useDownloadState();

    useEffect(() => {

        if (!invitePressed) {
            return;
        }

        setInvitePressed(false);
        setDownloading(true);

        sendProjectInvitation({
            request: () => new ProjectApi(getAuthorityConfiguration()).inviteUser({
                projectId: project.id,
                inviteUserInput: {
                    userEmailAddress: email,
                    roleId: selectedRole?.value === OWNER_ROLE_VALUE ? null : selectedRole?.value ?? null,
                    isOwner: selectedRole?.value === OWNER_ROLE_VALUE,
                }
            }),
            onSuccess: () => {
                resetCurrentUserProjects();
                setModalOpen(false);
                toast.success(t('auth.invitation-sent'));
            },
            onError: setError,
            onFinally: () => {
                setDownloading(false);
                onFinally?.();
            },
        });
    }, [invitePressed]);

    return (
        <>
            <Button onClick={() => setModalOpen(true)}>
                {t('project.invite-user')}
            </Button>
            {modalOpen &&
                <Modal
                    visible={modalOpen}
                    setVisible={setModalOpen}
                    loading={state.downloading}
                    title={t('project.send-invitation')}
                    className={'max-w-lg'}
                >
                    <div className="space-y-4">
                    {state.error !== null && <FormError error={state.error}/>}
                    <label className="block">
                        <span className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-200">{t('project.email')}</span>
                        <input
                            type="email"
                            value={email}
                            onChange={(event) => setEmail(event.target.value)}
                            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-800 shadow-none outline-none transition placeholder:text-slate-400 focus:border-violet-500 focus:ring-0 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                            placeholder={t('auth.email-address')}
                        />
                    </label>
                    <div>
                        <div className="mb-1.5 text-sm font-medium text-slate-700 dark:text-slate-200">{t('project.role')}</div>
                        <ProjectRoleSelect
                            options={canAssignAccess ? roleOptions : roleOptions.filter(({value}) => value === null)}
                            value={selectedRole}
                            onChange={setSelectedRole}
                            disabled={!canAssignAccess}
                        />
                        {!canAssignAccess && <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">{t('project.invitation-access-owner-only')}</p>}
                    </div>
                    <div className="flex justify-end pt-1">
                    <Button
                        onClick={() => {
                            setInvitePressed(true);
                        }}
                        disabled={!email.trim() || selectedRole === null}
                    >
                        {t('project.send-invitation')}
                    </Button>
                    </div>
                    </div>
                </Modal>
            }
        </>
    );
}

export async function sendProjectInvitation({request, onSuccess, onError, onFinally}) {
    try {
        await request();
        onSuccess();
    } catch (error) {
        onError(error);
    } finally {
        onFinally();
    }
}
